/**
 * useOrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineView as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity, OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineState =
  | 'idle';

export type OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent =
  | 'INIT'
  | 'ADVANCE'
  | 'MOVED';

type OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot = 'main';
interface OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineInternalState {
  machineState: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot, OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity>;
}

type OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[]; renderClear?: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineReducer(
  state: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineInternalState,
  action: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineAction
): OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  annotations: { states: { approved: { body: string }; draft: { body: string }; rejected: { body: string }; submitted: { body: string } }; transitions: { APPROVE: { body: string }; NEW_CLAIM: { body: string }; RAISE: { body: string }; REJECT: { body: string }; REVISE: { body: string }; SUBMIT: { body: string } } };
  initialState: string;
  machine: { emittedEvents: string[]; linkedEntity: string; listenedEvents: FieldValue[]; name: string; states: { isInitial: boolean; isTerminal: boolean; name: string }[]; transitions: Array<{ effects: { args: FieldValue[]; type: string }[]; event: string; from: string; guard: Array<number | string>; index: number; to: string } | { effects: { args: FieldValue[]; type: string }[]; event: string; from: string; index: number; to: string }> };
  showHeader: boolean;
  view: { children: { activeState: string; activeTransition: string; annotations: string; "aria-label": Array<string | string[]>; direction: Array<string | string[]>; showHeader: string; trait: string; type: string; visitedStates: string }[]; className: string; fullWidth: boolean; type: string };
}

const DEFAULT_ORB_DOC_TUTORIALS_BEGINNER_COMPLETE_ORBITAL_AR_DOC_EX1_APPROVAL_MACHINE_CONFIG = {
  annotations: { states: { approved: { body: "تمت الموافقة عليه." }, draft: { body: "المصروف قيد الكتابة." }, rejected: { body: "أُعيد للمراجعة." }, submitted: { body: "بانتظار القرار." } }, transitions: { APPROVE: { body: "يقبل المصروف ويسجّل القرار." }, NEW_CLAIM: { body: "يبدأ مصروفًا جديدًا." }, RAISE: { body: "يرفع المبلغ المطلوب أثناء الكتابة." }, REJECT: { body: "يرفض المصروف." }, REVISE: { body: "يعيد فتح المصروف المرفوض للتعديل." }, SUBMIT: { body: "يرسل المصروف للموافقة." } } },
  initialState: "draft",
  machine: { emittedEvents: ["MOVED"], linkedEntity: "Expense", listenedEvents: [], name: "Approval", states: [{ isInitial: true, isTerminal: false, name: "draft" }, { isInitial: false, isTerminal: false, name: "submitted" }, { isInitial: false, isTerminal: false, name: "approved" }, { isInitial: false, isTerminal: false, name: "rejected" }], transitions: [{ effects: [{ args: [], type: "render-ui" }], event: "INIT", from: "draft", index: 0, to: "draft" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "set" }], event: "RAISE", from: "draft", index: 1, to: "draft" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "render-ui" }], event: "SUBMIT", from: "draft", guard: [">", "@entity.amount", 0], index: 2, to: "submitted" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "set" }, { args: [], type: "render-ui" }], event: "APPROVE", from: "submitted", guard: ["<=", "@entity.amount", 500], index: 3, to: "approved" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "set" }, { args: [], type: "render-ui" }], event: "REJECT", from: "submitted", index: 4, to: "rejected" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "set" }, { args: [], type: "render-ui" }], event: "NEW_CLAIM", from: "approved", index: 5, to: "draft" }, { effects: [{ args: [], type: "emit" }, { args: [], type: "render-ui" }], event: "REVISE", from: "rejected", index: 6, to: "draft" }] },
  showHeader: true,
  view: { children: [{ activeState: "@entity.active", activeTransition: "@entity.fired", annotations: "@config.annotations", "aria-label": ["str/concat", ["object/get", "@config.machine", "name"], " — ", ["i18n/t", "std-orb-live-machine:diagram.label"]], direction: ["if", ["==", "@locale", "ar"], "rtl", "ltr"], showHeader: "@config.showHeader", trait: "@config.machine", type: "avl-state-machine", visitedStates: "@entity.visited" }], className: "overflow-x-auto", fullWidth: true, type: "box" },
} as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineConfig;

export interface OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineLogicReturn {
  state: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot, OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ADVANCE', 'MOVED']);

export function useOrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineLogic(config?: Partial<OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineConfig>): OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineLogicReturn {
  const mergedConfig: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineConfig = { ...DEFAULT_ORB_DOC_TUTORIALS_BEGINNER_COMPLETE_ORBITAL_AR_DOC_EX1_APPROVAL_MACHINE_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-beginner-complete-orbital-ar-doc-ex1-approval-machine', orbitalName: 'OrbDocTutorialsBeginnerCompleteOrbitalAR', traitName: 'OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['active'] = "draft";
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['fired'] = -1;
        fields = { ...(fields ?? {}), 'fired': _fields['fired'] } as Record<string, EventPayloadValue | undefined>;
        _fields['visited'] = ["draft"];
        fields = { ...(fields ?? {}), 'visited': _fields['visited'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'ADVANCE' && (((getNestedValue(JSON.parse("{\"emittedEvents\":[\"MOVED\"],\"linkedEntity\":\"Expense\",\"listenedEvents\":[],\"name\":\"Approval\",\"states\":[{\"isInitial\":true,\"isTerminal\":false,\"name\":\"draft\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"submitted\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"approved\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"rejected\"}],\"transitions\":[{\"effects\":[{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"INIT\",\"from\":\"draft\",\"index\":0,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"}],\"event\":\"RAISE\",\"from\":\"draft\",\"index\":1,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"SUBMIT\",\"from\":\"draft\",\"guard\":[\">\",\"\\u0040entity.amount\",0],\"index\":2,\"to\":\"submitted\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"APPROVE\",\"from\":\"submitted\",\"guard\":[\"<=\",\"\\u0040entity.amount\",500],\"index\":3,\"to\":\"approved\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"REJECT\",\"from\":\"submitted\",\"index\":4,\"to\":\"rejected\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"NEW_CLAIM\",\"from\":\"approved\",\"index\":5,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"REVISE\",\"from\":\"rejected\",\"index\":6,\"to\":\"draft\"}]}"), "transitions") as readonly EventPayloadValue[] | undefined) ?? []).some((t) => ((getNestedValue(t, "event") === payloadObj?.event) && (getNestedValue(t, "from") === fields?.active))))) {
      // idle → idle (on ADVANCE)
        {

        const move = ((getNestedValue(JSON.parse("{\"emittedEvents\":[\"MOVED\"],\"linkedEntity\":\"Expense\",\"listenedEvents\":[],\"name\":\"Approval\",\"states\":[{\"isInitial\":true,\"isTerminal\":false,\"name\":\"draft\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"submitted\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"approved\"},{\"isInitial\":false,\"isTerminal\":false,\"name\":\"rejected\"}],\"transitions\":[{\"effects\":[{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"INIT\",\"from\":\"draft\",\"index\":0,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"}],\"event\":\"RAISE\",\"from\":\"draft\",\"index\":1,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"SUBMIT\",\"from\":\"draft\",\"guard\":[\">\",\"\\u0040entity.amount\",0],\"index\":2,\"to\":\"submitted\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"APPROVE\",\"from\":\"submitted\",\"guard\":[\"<=\",\"\\u0040entity.amount\",500],\"index\":3,\"to\":\"approved\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"REJECT\",\"from\":\"submitted\",\"index\":4,\"to\":\"rejected\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"set\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"NEW_CLAIM\",\"from\":\"approved\",\"index\":5,\"to\":\"draft\"},{\"effects\":[{\"args\":[],\"type\":\"emit\"},{\"args\":[],\"type\":\"render-ui\"}],\"event\":\"REVISE\",\"from\":\"rejected\",\"index\":6,\"to\":\"draft\"}]}"), "transitions") as readonly EventPayloadValue[] | undefined) ?? []).find((t) => ((getNestedValue(t, "event") === payloadObj?.event) && (getNestedValue(t, "from") === fields?.active)));

        _fields['fired'] = getNestedValue(move, "index");
        fields = { ...(fields ?? {}), 'fired': _fields['fired'] } as Record<string, EventPayloadValue | undefined>;
        _fields['visited'] = ((getNestedValue(move, "to") === "draft") ? ["draft"] : [...((fields?.visited as readonly EventPayloadValue[] | undefined) ?? []), getNestedValue(move, "to")]);
        fields = { ...(fields ?? {}), 'visited': _fields['visited'] } as Record<string, EventPayloadValue | undefined>;
        _fields['active'] = getNestedValue(move, "to");
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        }

      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[]; clear: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'ADVANCE': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[] };
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'ADVANCE': (payload?: OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachineEventPayloadMap['ADVANCE']) => dispatch('ADVANCE', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsBeginnerCompleteOrbitalAR.OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine', ['INIT', 'ADVANCE'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'ADVANCE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'MOVED' (from OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1Approval) → trigger 'ADVANCE'
    const unsub_moved_from_orbdoctutorialsbeginnercompleteorbitalardocex1approval_to_advance = eventBus.on('UI:OrbDocTutorialsBeginnerCompleteOrbitalAR.OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1Approval.MOVED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'OrbDocTutorialsBeginnerCompleteOrbitalAR.OrbDocTutorialsBeginnerCompleteOrbitalARDocEx1ApprovalMachine', 'ADVANCE')) return;
      enqueueEvent('ADVANCE', { event: (event.payload as EventPayload | undefined)?.event } as EventPayload);
    });
    return () => {
      if (typeof unsub_moved_from_orbdoctutorialsbeginnercompleteorbitalardocex1approval_to_advance === 'function') unsub_moved_from_orbdoctutorialsbeginnercompleteorbitalardocex1approval_to_advance();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
